import { useCallback, useRef, useState, useEffect } from "react";
import { usePaginationFragment } from "react-relay";
import { useDispatch } from "react-redux";
import { defineMessages } from "react-intl";
import { isNumber } from "lodash";

import { useFormatMessage } from "../../Intl/Context";
import { isResourcesListFiltered } from "../../Utils/resourcesList";
import { updateResourcesListItemsCount } from "../../../reducers/resourcesList/items";
import {
  useResourcesList,
  useResourcesListFilteringRefetched
} from "../../ResourcesList/resourcesList";
import { useStoreCurrentContributionsConnection } from "../../Utils/contributionsConnections";
import { CONTRIBUTIONS_CONNECTION_TYPE_CONTRIBUTIONS } from "../../../reducers/contributionsConnections";

export const CONTRIBUTIONS_DEFAULT_BATCH_SIZE = 10;

// This hook allows managing the actions needed to load a paginatable and filterable list
// of contributions. It handles the needed boilerplate to setup Relay and returns an
// object that contains the following keys :
//
//  - contributions : The current contributions list filtered and/or paginated
//  - isLoading : Whether the list is currently reloading or loading more results
//  - loadMore : A function that loads the next "page" of contributions
//  - emptyListMessage : A string containing a message to be displayed when the list is
//                       empty. Note that if there are contributions, this key contains
//                       `null`.
//
// The hook takes 5 arguments :
//
//  - fragment                       : a Relay refetchable fragment that's used to load
//                                     the contributions.
//
//  - contribuable                   : the contribuable (participative module or theme)
//                                     reference to be provided to the fragment.
//
//  - contributionsBatchSize         : (optional) the number of contributions to load for
//                                     each contributions page. If not provided, it
//                                     defaults to 10.
//
//  - contributionsSelector          : (optional) a predicate that receives the fetched
//                                     `contribuable` as argument and should return
//                                     a relay connection containing the contributions. If
//                                     not provided, it defaults to
//                                     `contribuable.contributions`.
//
//  - fetchPolicy                    : (optional) the Relay's `fetchPolicy` used to fetch
//                                     the contributions when the filter contributions
//                                     loader query has returned. If not provided, it
//                                     defaults to "store-only".
//
//  - connectionThemeId              : (optional) the theme id that the contributions
//                                     connection will be associated to, for caching
//                                     purposes. This allows new contributions to be
//                                     appended to the theme contributions fragments that
//                                     are already cached by Relay.
//
//  - onRefetchedAfterSortingChanged : (optional) allows providing a callback which will
//                                     be called when the fragment was refetched after the
//                                     contributions list sorting was changed.
//
const useFilterableContributionsList = props => {
  const dispatch = useDispatch();
  // The fragment prop must provide a refetchable fragment on the  which takes the
  // following variables :
  //
  //  - $contributionsCount   : The number of contributions to load
  //  - $contributionsCursor  : The current pagination cursor
  //  - $contributionsFilters : A ContributionsListFiltersInput instance
  //  - $contributionsSort    : A ContributionsSortInput instance
  //
  const { fragment } = props;

  // These callbacks allow hooking into
  const { onRefetchedAfterSortingChanged, onRefetchedAfterFiltersChanged } = props;

  // By default, contributions are fetched from the `contribuable.contributions`
  // property, but in the case of alternative visualization behaviors, like for the
  // map contributions markers, we let the parent component define a selector that returns
  // a contributions connection, which will be returned by the hook.
  const contributionsSelector =
    props.contributionsSelector || (contribuable => contribuable.contributions);

  // By default, the filterable contributions list itself never calls the server to fetch
  // the contributions list, as the filters contributions loaders take care of that job.
  //
  // The "store-only" fetch policy allows the connection to never launch a network
  // request and just wait for the filter bar launched request to be loaded into the
  // store, and then update the connection here.
  //
  // But when multiple contributions lists are not synchronized between each other, but
  // we have a single filtering system, like for map contributions and markers, we let
  // the parent component provide an alternative fetch policy
  const fetchPolicy = props.fetchPolicy || "store-only";

  const contributionsBatchSize = isNumber(props.contributionsBatchSize)
    ? props.contributionsBatchSize
    : CONTRIBUTIONS_DEFAULT_BATCH_SIZE;

  const {
    data: contribuable,
    loadNext,
    hasNext,
    isLoadingNext,
    loadPrevious,
    hasPrevious,
    isLoadingPrevious,
    refetch
  } = usePaginationFragment(fragment, props.contribuable);

  const contributions = contributionsSelector(contribuable);
  // This is the contributions cache that's used to display the contributions, even when
  // the contributions connection is reloading because of the filters refetching the
  // connection.
  const contributionEdgesRef = useRef(contributions?.edges || []);

  // If the contributions key is not null, this means that we're not loading new
  // contributions so we can keep the edges up to date
  if (contributions) {
    contributionEdgesRef.current = contributions.edges;
  }

  const [isLoading, setIsLoading] = useState(!contributions);
  const contributionEdges = contributionEdgesRef.current;

  const resourcesList = useResourcesList();
  const isFiltered =
    isResourcesListFiltered(resourcesList) || isLoading || !contributions;

  const hasMoreToLoad = contributionEdges.length > 0 && hasNext;
  const hasPreviousToLoad = contributionEdges.length > 0 && hasPrevious;

  const refetchingAfterFiltersChangedRef = useRef(false);
  const refetchingAfterSortingChangedRef = useRef(false);

  const handleFilteredResourcesBeforeRefetch = useCallback(
    (_, { filtersChanged, sortingChanged }) => {
      setIsLoading(true);

      if (filtersChanged) {
        refetchingAfterFiltersChangedRef.current = true;
      }

      if (sortingChanged) {
        refetchingAfterSortingChangedRef.current = true;
      }
    },
    []
  );

  const handleFilteredResourcesLoaded = useCallback(
    ({ filters: contributionsFilters, sort: contributionsSort }) => {
      refetch(
        { contributionsFilters, contributionsSort },
        {
          fetchPolicy,
          onComplete: () => {
            setIsLoading(false);

            if (refetchingAfterFiltersChangedRef.current) {
              refetchingAfterFiltersChangedRef.current = false;

              if (onRefetchedAfterFiltersChanged) {
                onRefetchedAfterFiltersChanged(contributionsFilters);
              }
            }

            if (refetchingAfterSortingChangedRef.current) {
              refetchingAfterSortingChangedRef.current = false;

              if (onRefetchedAfterSortingChanged) {
                onRefetchedAfterSortingChanged(contributionsSort);
              }
            }
          }
        }
      );
    },
    [fetchPolicy, onRefetchedAfterFiltersChanged, onRefetchedAfterSortingChanged, refetch]
  );

  // The useStoreCurrentContributionsConnection allows tracking the current
  // contributions' connection id into a global store, so it can be used to manipulate
  // the connection when a new contribution is submitted, so that it can be added to the
  // connection.
  const connectionType =
    props.connectionType || CONTRIBUTIONS_CONNECTION_TYPE_CONTRIBUTIONS;
  useStoreCurrentContributionsConnection(
    contribuable,
    connectionType,
    contributions,
    props.connectionThemeId
  );

  useResourcesListFilteringRefetched({
    beforeRefetch: handleFilteredResourcesBeforeRefetch,
    onRefetched: handleFilteredResourcesLoaded
  });

  const loadMore = useCallback(() => {
    if (!hasMoreToLoad || isLoadingNext) return;

    loadNext(contributionsBatchSize);
  }, [contributionsBatchSize, hasMoreToLoad, isLoadingNext, loadNext]);

  const loadPreviousPage = useCallback(() => {
    if (!hasPreviousToLoad || isLoadingPrevious) return;

    loadPrevious(contributionsBatchSize);
  }, [contributionsBatchSize, hasPreviousToLoad, isLoadingPrevious, loadPrevious]);

  const t = useFormatMessage();
  const searchResultsEmpty = t(intlMessages.searchResultsEmpty);
  const noContributions = t(intlMessages.noContributions);
  const emptyListMessage =
    contributionEdges.length === 0 && (isFiltered ? searchResultsEmpty : noContributions);

  const contributionNodes = contributionEdges.map(({ node }) => node);
  const { totalCount = 0 } = contributions || {};
  const { resourcesListKey } = resourcesList;

  useEffect(() => {
    dispatch(updateResourcesListItemsCount(resourcesListKey, totalCount));
  }, [dispatch, resourcesListKey, totalCount]);

  return {
    contributions: contributionNodes,
    totalCount,
    isLoading,
    hasMoreToLoad,
    loadMore,
    hasPreviousToLoad,
    loadPreviousPage,
    isLoadingNext,
    isLoadingPrevious,
    emptyListMessage
  };
};

export default useFilterableContributionsList;

const intlMessages = defineMessages({
  searchResultsEmpty: {
    id: "participativeModule.contributionsList.searchResultsEmpty",
    defaultMessage: `
      Aucun résultat pour votre recherche, merci d'essayer avec d'autres
      filtres ou critères de recherche.
    `
  },
  noContributions: {
    id: "participativeModule.contributionsList.noContributions",
    defaultMessage: `
      Aucune contribution n'a été déposée pour le moment.
    `
  }
});
